import React, { useEffect, useState } from 'react';
import { FaQuoteLeft, FaBrain, FaGraduationCap } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header';
import { useOrgAuthUi } from '../branding/AuthOrgBrand';

const FALLBACK_QUOTES = [
    { text: 'Education is not the filling of a pail, but the lighting of a fire.', author: 'William Butler Yeats' },
];

/**
 * Auth layout with the classic two-column design:
 * form card + rotating quote / feature tiles (desktop).
 */
export default function AuthShell({
    children,
    /** i18n namespace for quotes/features: 'signin' | 'signup' */
    quoteKey = 'signin',
    /** Hide marketing column (e.g. compact tenant flows) */
    showMarketing = true,
}) {
    const { t } = useTranslation();
    const { primary } = useOrgAuthUi();
    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

    const quotesData = t(`${quoteKey}.quotes`, { returnObjects: true });
    const quotes = Array.isArray(quotesData) && quotesData.length > 0 ? quotesData : FALLBACK_QUOTES;

    useEffect(() => {
        if (!showMarketing || quotes.length < 2) return undefined;
        const interval = setInterval(() => {
            setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [showMarketing, quotes.length]);

    const quote = quotes[currentQuoteIndex] || FALLBACK_QUOTES[0];

    return (
        <div className="min-h-screen relative overflow-hidden bg-[#F7F7F7] dark:bg-gray-900">
            <div
                className="pointer-events-none absolute inset-0 opacity-80"
                style={{
                    background: `radial-gradient(ellipse 70% 45% at 15% 0%, ${primary}18 0%, transparent 55%), radial-gradient(ellipse 50% 35% at 100% 80%, ${primary}12 0%, transparent 50%)`,
                }}
                aria-hidden
            />
            <Header isHome={false} />
            <main className="relative z-10 container mx-auto px-4 pt-24 sm:pt-32 pb-12">
                <div className="max-w-6xl mx-auto">
                    <div className={`grid gap-12 items-center ${showMarketing ? 'lg:grid-cols-2' : 'max-w-md mx-auto'}`}>
                        <div>{children}</div>

                        {showMarketing ? (
                            <aside className="hidden lg:block space-y-6" aria-label={t('auth.shell.inspiration')}>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 min-h-[8.5rem] transition-opacity duration-500">
                                    <div className="flex items-start gap-3">
                                        <FaQuoteLeft
                                            className="w-5 h-5 flex-shrink-0 mt-1"
                                            style={{ color: primary }}
                                        />
                                        <div>
                                            <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white mb-3 leading-relaxed">
                                                {quote?.text || FALLBACK_QUOTES[0].text}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-300">
                                                — {quote?.author || FALLBACK_QUOTES[0].author}
                                            </p>
                                        </div>
                                    </div>
                                    {quotes.length > 1 ? (
                                        <div className="mt-4 flex gap-1.5 justify-end">
                                            {quotes.map((_, i) => (
                                                <span
                                                    key={i}
                                                    className={`h-1.5 rounded-full transition-all ${
                                                        i === currentQuoteIndex ? 'w-4' : 'w-1.5 bg-gray-300 dark:bg-gray-600'
                                                    }`}
                                                    style={i === currentQuoteIndex ? { backgroundColor: primary } : undefined}
                                                    aria-hidden
                                                />
                                            ))}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200">
                                        <FaBrain className="w-6 h-6 mb-3" style={{ color: primary }} />
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                            {t(`${quoteKey}.features.aiLearning.title`)}
                                        </h3>
                                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {t(`${quoteKey}.features.aiLearning.description`)}
                                        </p>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200">
                                        <FaGraduationCap className="w-6 h-6 mb-3" style={{ color: primary }} />
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                            {t(`${quoteKey}.features.expertGuidance.title`)}
                                        </h3>
                                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {t(`${quoteKey}.features.expertGuidance.description`)}
                                        </p>
                                    </div>
                                </div>
                            </aside>
                        ) : null}
                    </div>
                </div>
            </main>
        </div>
    );
}

export function AuthCard({ children, className = '' }) {
    return (
        <div
            className={`bg-white dark:bg-gray-800 rounded-xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}
        >
            {children}
        </div>
    );
}

export function AuthField({
    id,
    label,
    error,
    type = 'text',
    value,
    onChange,
    placeholder,
    autoComplete,
    trailing,
    className = '',
}) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                {label}
            </label>
            <div className="relative">
                <input
                    id={id}
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    className={`w-full rounded-xl border bg-white dark:bg-slate-950 px-3.5 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--org-primary,#2563eb)]/35 focus:border-[var(--org-primary,#2563eb)] transition-shadow ${
                        trailing ? 'pe-11' : ''
                    } ${
                        error
                            ? 'border-red-400 focus:ring-red-400/40 focus:border-red-400'
                            : 'border-slate-200 dark:border-slate-600'
                    }`}
                />
                {trailing}
            </div>
            {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
        </div>
    );
}
